import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Phone, Video, Unlock, Mic, MicOff, Camera, Loader2, CheckCircle } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';
import useAuthStore from '../stores/authStore';
import useStore from '../stores/useStore';
import WebRTCPlayer from '../components/WebRTCPlayer';
import CcmLicenseBanner from '../components/CcmLicenseBanner';
import useIntercomControls from '../hooks/useIntercomControls';

/**
 * Full-page intercom answer view. Renders in normal document flow inside the app
 * shell (Layout -> .main-content -> Outlet), so it inherits the working bottom
 * nav and page scrolling. This replaces the old floating modal, which could not
 * be sized correctly on the iOS PWA because no CSS viewport unit reports the
 * true usable height. No overlay, no position:fixed, no viewport-height units,
 * no scroll-lock here on purpose. Mirrors the cameras page (ArchivePlaybackNew)
 * full-page video layout, using the same WebRTCPlayer, live on mount.
 */
export default function IntercomView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenantVersion } = useAuthStore();
  const { waveConfig, setWaveConfig } = useStore();

  const [intercom, setIntercom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLiveView, setShowLiveView] = useState(false);

  const { unlocking, unlockSuccess, handleUnlock, handleTalk, callState, activeIntercomId } = useIntercomControls();

  // Tag <body> while this page is mounted so landscape CSS can full-screen the
  // view and hide the shared bottom nav, then revert on navigate-away. Separate,
  // unconditional effect; cleanup MUST remove the class so the nav returns
  // everywhere else.
  useEffect(() => {
    document.body.classList.add('intercom-active');
    return () => document.body.classList.remove('intercom-active');
  }, []);

  // Auto-enable live view once we actually have something to show. Prevents
  // the state from claiming "on" before the async intercom/waveConfig loads
  // resolve, which was making the first tap look like it turned live view off.
  useEffect(() => {
    if (intercom?.cameraId && waveConfig?.host) {
      setShowLiveView(true);
    }
  }, [intercom?.cameraId, waveConfig?.host]);

  // Self-sufficient load: works when navigated from the list AND on a direct
  // deep-link (push notification / hard refresh). There is no single-intercom
  // endpoint, so we read the same integrations list Intercoms.jsx uses and find
  // by id, plus tenant config for the WebRTC waveConfig.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const cfgRes = await fetchWithTenant('/api/tenant/config');
        const cfg = await cfgRes.json();
        if (!cancelled && cfg.waveConfig) setWaveConfig(cfg.waveConfig);
      } catch (e) {
        console.warn('[IntercomView] tenant config load failed:', e.message);
      }
      try {
        const intRes = await fetchWithTenant('/api/integrations');
        if (intRes.ok) {
          const intData = await intRes.json();
          const found = (intData.integrations || [])
            .filter(i => i.categoryName === 'INTERCOM')
            .map(i => ({
              id: i.id,
              name: i.name || i.config?.name || 'Intercom',
              cameraId: i.config?.cameraId || '',
              n8nWebhookUrl: i.config?.n8nWebhookUrl || '',
            }))
            .find(i => i.id === id);
          if (!cancelled) setIntercom(found || null);
        }
      } catch (e) {
        console.warn('[IntercomView] integrations load failed:', e.message);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [id, tenantVersion, setWaveConfig]);

  const goBack = () => navigate('/intercoms');

  const isCalling = callState === 'active' && activeIntercomId === (intercom?.id);

  return (
    <div className="md-page intercom-view">
      <CcmLicenseBanner />

      <div className="intercom-view-header">
        <button className="intercom-view-back-btn" onClick={goBack} aria-label="Back to intercoms">
          <ChevronLeft size={24} />
        </button>
        <span className="intercom-view-name">
          <Phone size={18} />
          {intercom?.name || 'Intercom'}
        </span>
      </div>

      <div className="intercom-view-video">
        {loading ? (
          <div className="camera-placeholder">
            <Loader2 size={48} className="spin" />
            <span>Loading...</span>
          </div>
        ) : !intercom ? (
          <div className="camera-placeholder">
            <Camera size={48} />
            <span>Intercom not found</span>
          </div>
        ) : showLiveView && intercom.cameraId && waveConfig?.host ? (
          <WebRTCPlayer
            camera={{ id: intercom.cameraId, name: intercom.name }}
            stream="primary"
            muted={false}
          />
        ) : showLiveView && intercom.cameraId && !waveConfig?.host ? (
          <div className="camera-placeholder">
            <Loader2 size={48} className="spin" />
            <span>Loading video...</span>
          </div>
        ) : (
          <div className="camera-placeholder">
            <Camera size={48} />
            <span>Live view off</span>
          </div>
        )}
      </div>

      <div className="intercom-view-controls">
        <button
          className={`btn btn-lg ${showLiveView ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setShowLiveView(!showLiveView)}
          disabled={!intercom?.cameraId}
        >
          <Video size={18} />
          {showLiveView ? 'Live Video' : 'Show Live'}
        </button>

        <button
          className={`btn btn-lg ${unlockSuccess ? 'btn-success' : 'btn-primary'}`}
          onClick={() => intercom && handleUnlock(intercom.id)}
          disabled={unlocking || !intercom?.n8nWebhookUrl}
        >
          {unlocking ? (
            <Loader2 size={18} className="spin" />
          ) : unlockSuccess ? (
            <CheckCircle size={18} />
          ) : (
            <Unlock size={18} />
          )}
          {unlockSuccess ? 'Unlocked!' : 'Unlock Door'}
        </button>

        <button
          className={`btn btn-lg ${isCalling ? 'btn-danger' : 'btn-primary'}`}
          onClick={() => intercom && handleTalk(intercom)}
          disabled={!intercom?.cameraId || (callState === 'active' && activeIntercomId !== intercom?.id)}
        >
          {isCalling ? <MicOff size={18} /> : <Mic size={18} />}
          {isCalling ? 'End Call' : 'Talk'}
        </button>
      </div>
    </div>
  );
}
